"use client";

import { useState, useCallback } from "react";
import {
  initialCalculatorState,
  inputDigit as logicInputDigit,
  inputDecimal as logicInputDecimal,
  clearAll as logicClearAll,
  applyOperator as logicApplyOperator,
  equals as logicEquals,
  type OperatorSymbol,
  type CalculatorState,
} from "@/lib/calculator";

export default function Calculator() {
  const [state, setState] = useState<CalculatorState>(initialCalculatorState);

  const inputDigit = useCallback((digit: string) => {
    setState((s) => logicInputDigit(s, digit));
  }, []);

  const inputDecimal = useCallback(() => {
    setState((s) => logicInputDecimal(s));
  }, []);

  const clearAll = useCallback(() => {
    setState(() => logicClearAll());
  }, []);

  const handleOperator = useCallback((nextOperator: OperatorSymbol) => {
    setState((s) => logicApplyOperator(s, nextOperator));
  }, []);

  const handleEquals = useCallback(() => {
    setState((s) => logicEquals(s));
  }, []);

  const toggleSign = useCallback(() => {
    setState((s) => {
      const dv = s.displayValue;
      if (dv === "0") return s;
      return {
        ...s,
        displayValue: dv.startsWith("-") ? dv.slice(1) : `-${dv}`,
      };
    });
  }, []);

  const applyPercent = useCallback(() => {
    setState((s) => {
      const v = parseFloat(s.displayValue);
      if (Number.isNaN(v)) return s;
      return { ...s, displayValue: String(v / 100) };
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const { key } = e;
      if (key >= "0" && key <= "9") {
        e.preventDefault();
        inputDigit(key);
        return;
      }
      if (key === ".") {
        e.preventDefault();
        inputDecimal();
        return;
      }
      if (key === "+" || key === "-" || key === "*" || key === "/") {
        e.preventDefault();
        handleOperator(key as OperatorSymbol);
        return;
      }
      if (key === "Enter" || key === "=") {
        e.preventDefault();
        handleEquals();
        return;
      }
      if (key === "Escape" || key.toLowerCase() === "c") {
        e.preventDefault();
        clearAll();
      }
    },
    [inputDigit, inputDecimal, handleOperator, handleEquals, clearAll]
  );

  return (
    <div
      className="w-full max-w-[320px] rounded-lg border border-border p-4 shadow-sm bg-card text-foreground"
      role="application"
      aria-label="Calculator"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="w-full h-14 rounded-md border border-border flex items-center justify-end px-3 text-2xl font-mono bg-white/70 dark:bg-black/30 overflow-hidden"
      >
        {state.displayValue}
      </div>
      <div className="mt-3 grid grid-cols-4 gap-2">
        <button
          className="col-span-1 h-12 rounded-md bg-calc-func text-calc-func-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={clearAll}
          aria-label="Clear all"
        >
          C
        </button>
        <button
          className="col-span-1 h-12 rounded-md bg-calc-func text-calc-func-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={toggleSign}
          aria-label="Toggle sign"
        >
          ±
        </button>
        <button
          className="col-span-1 h-12 rounded-md bg-calc-func text-calc-func-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={applyPercent}
          aria-label="Percent"
        >
          %
        </button>
        <button
          className="col-span-1 h-12 rounded-md bg-calc-operator text-calc-operator-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => handleOperator("/" as OperatorSymbol)}
          aria-label="Divide"
        >
          ÷
        </button>

        {["7", "8", "9"].map((d) => (
          <button
            key={d}
            className="h-12 rounded-md bg-calc-key hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={() => inputDigit(d)}
            aria-label={`Digit ${d}`}
          >
            {d}
          </button>
        ))}
        <button
          className="h-12 rounded-md bg-calc-operator text-calc-operator-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => handleOperator("*" as OperatorSymbol)}
          aria-label="Multiply"
        >
          ×
        </button>

        {["4", "5", "6"].map((d) => (
          <button
            key={d}
            className="h-12 rounded-md bg-calc-key hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={() => inputDigit(d)}
            aria-label={`Digit ${d}`}
          >
            {d}
          </button>
        ))}
        <button
          className="h-12 rounded-md bg-calc-operator text-calc-operator-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => handleOperator("-" as OperatorSymbol)}
          aria-label="Subtract"
        >
          −
        </button>

        {["1", "2", "3"].map((d) => (
          <button
            key={d}
            className="h-12 rounded-md bg-calc-key hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            onClick={() => inputDigit(d)}
            aria-label={`Digit ${d}`}
          >
            {d}
          </button>
        ))}
        <button
          className="h-12 rounded-md bg-calc-operator text-calc-operator-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => handleOperator("+" as OperatorSymbol)}
          aria-label="Add"
        >
          +
        </button>

        <button
          className="col-span-2 h-12 rounded-md bg-calc-key hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={() => inputDigit("0")}
          aria-label="Digit 0"
        >
          0
        </button>
        <button
          className="h-12 rounded-md bg-calc-key hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={inputDecimal}
          aria-label="Decimal"
        >
          .
        </button>
        <button
          className="h-12 rounded-md bg-calc-operator text-calc-operator-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={handleEquals}
          aria-label="Equals"
        >
          =
        </button>
      </div>
    </div>
  );
}
